import { CalendarPlus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { useSearchParams } from '@/lib/scopedParams';
import { api } from '../../../api';
import type { BackorderStatus, DropSource, DropStatus } from '../../../api/areas/market';
import { Callout, Field, Section } from '../../../components/blocks';
import { FactRows } from '../../../components/flow';
import { PageHeader } from '../../../components/PageHeader';
import { useWrite, WriteError } from '../../../components/Write';
import { useAccess } from '../../../lib/access';
import { ActionDialog, FormFooter, selectClass, type DialogBodyProps } from '../../operator/shared';
import { RefreshButton } from '../../registry/shared';
import { BackorderLinks, StatusDomainFilter } from '../module';
import { BACKORDER_STATUS, CATCH_STATUS, DROP_STATUS, HOLD_STATUS, marketEnum } from '../shared';
import { BackordersTable, DropsTable } from './tables';

function useListParams() {
  const [params, setParams] = useSearchParams();
  const set = (entries: Record<string, string>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(entries)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p);
  };
  return { status: params.get('status') ?? '', domain: params.get('domain') ?? '', set };
}

const SOURCES: DropSource[] = ['OPERATOR', 'REGISTRY_NOTICE'];

/** The local date-time of a `datetime-local` input as an ISO instant. */
function localToIso(v: string): string | null {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function ScheduleDropForm({ close, changed }: DialogBodyProps) {
  const { t } = useTranslation('market');
  const [domain, setDomain] = useState('');
  const [when, setWhen] = useState('');
  const [source, setSource] = useState<DropSource>('REGISTRY_NOTICE');
  const [reference, setReference] = useState('');
  const w = useWrite((_: null, opts) => api.scheduleDrop({ domain: domain.trim(), expected_drop_at: localToIso(when) ?? '', source, source_reference: reference.trim() || null }, opts));
  const done = w.state.phase === 'done' ? w.state.result : undefined;
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run(null).then((r) => r && changed());
      }}
    >
      {done ? (
        <Callout tone="ok">{t('bo.drops.scheduled', { domain: done.domain_name })}</Callout>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{t('bo.drops.scheduleHint')}</p>
          <Field label={t('backorders.domain')}>
            <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="example.com" autoComplete="off" required />
          </Field>
          <Field label={t('bo.drops.expected')} hint={t('bo.drops.expectedHint')}>
            <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} required />
          </Field>
          <Field label={t('backorders.source')}>
            <select className={selectClass} value={source} onChange={(e) => setSource(e.target.value as DropSource)}>
              {SOURCES.map((s) => (
                <option key={s} value={s}>
                  {marketEnum('dropSource', s)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('bo.drops.reference')} hint={t('bo.drops.referenceHint')}>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={200} />
          </Field>
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!domain.trim() || !localToIso(when)} submit={done ? undefined : t('bo.drops.schedule')} onClose={close} />
    </form>
  );
}

/** `/backorders?tab=drops`, reference 59: every expected drop with its catch attempt, and recording a new one. */
export function DropListPage() {
  const { t } = useTranslation('market');
  const access = useAccess();
  const { status, domain, set } = useListParams();
  const [reloadKey, setReloadKey] = useState(0);
  const canManage = !access.client && access.has(['auction.manage']);
  return (
    <>
      <PageHeader
        title={t('bo.drops.title')}
        subtitle={t('bo.drops.subtitle')}
        actions={
          <>
            <RefreshButton onClick={() => setReloadKey((n) => n + 1)} />
            {canManage ? (
              <ActionDialog label={t('bo.drops.schedule')} icon={CalendarPlus} variant="default" title={t('bo.drops.scheduleTitle')} onChanged={() => setReloadKey((n) => n + 1)}>
                {(p) => <ScheduleDropForm {...p} />}
              </ActionDialog>
            ) : null}
          </>
        }
      />
      <BackorderLinks here="drops" />
      <StatusDomainFilter
        status={status}
        domain={domain}
        label={t('backorders.status')}
        statuses={(Object.keys(DROP_STATUS) as DropStatus[]).map((s) => ({ value: s, label: marketEnum('dropStatus', s) }))}
        onChange={set}
      />
      <div className="space-y-4">
        <Section flush title={t('backorders.drops')} description={t('bo.drops.listHint')}>
          <DropsTable key={reloadKey} status={status} domain={domain} />
        </Section>
        <Callout tone="info" title={t('bo.drops.dataTitle')}>
          {t('backorders.dropTimesNote')}
        </Callout>
      </div>
    </>
  );
}

/** `/backorders?tab=requests`, reference 60: every customer's backorder, with the states kept apart. */
export function BackorderRequestsPage() {
  const { t } = useTranslation('market');
  const { status, domain, set } = useListParams();
  const [reloadKey, setReloadKey] = useState(0);
  const enumList = (group: 'backorderStatus' | 'catchStatus' | 'holdStatus', keys: string[]) => keys.map((k) => marketEnum(group, k)).join(' · ');
  return (
    <>
      <PageHeader title={t('bo.requests.title')} subtitle={t('bo.requests.subtitle')} actions={<RefreshButton onClick={() => setReloadKey((n) => n + 1)} />} />
      <BackorderLinks here="requests" />
      <StatusDomainFilter
        status={status}
        domain={domain}
        label={t('backorders.status')}
        statuses={(Object.keys(BACKORDER_STATUS) as BackorderStatus[]).map((s) => ({ value: s, label: marketEnum('backorderStatus', s) }))}
        onChange={set}
      />
      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_340px] [&>*]:min-w-0">
        <Section flush title={t('backorders.backorders')}>
          <BackordersTable key={reloadKey} status={status} domain={domain} />
        </Section>
        <Section title={t('bo.requests.statesTitle')} description={t('bo.requests.statesHint')}>
          <FactRows
            rows={[
              [t('bo.requests.stateRequest'), enumList('backorderStatus', Object.keys(BACKORDER_STATUS))],
              [t('bo.requests.stateCatch'), enumList('catchStatus', Object.keys(CATCH_STATUS))],
              [t('bo.requests.stateMoney'), enumList('holdStatus', Object.keys(HOLD_STATUS))],
            ]}
          />
        </Section>
      </div>
    </>
  );
}
